import { useNavigate } from "@remix-run/react";
import { useCallback } from "react";

import { buildTourConfig } from "#app/components/tours/build-first-agent-tour";
import { ensureTourI18nReady } from "#app/components/tours/sandbox-simulator";
import { runTour } from "#app/components/tours/tour-provider";
import { useAgentStore } from "#app/store/agent";
import { useChatStore } from "#app/store/chat";

/**
 * Drives the "Build your first agent" product tour.
 *
 * The tour is a scripted replay: it navigates the app and pushes mock messages,
 * todos and a planned workflow straight into the chat/agent stores so the user
 * sees a realistic run without calling the backend. Extracted from side-bar.tsx
 * so the sidebar file is about layout.
 */
export function useStartTour() {
  const navigate = useNavigate();

  const addMessage = useChatStore((s) => s.addMessage);
  const updateChatStore = useChatStore((s) => s.updateChatStore);
  const setCurrentThinking = useChatStore((s) => s.setCurrentThinking);
  const addOrUpdateTodoOnLastAssistantMessage = useChatStore(
    (s) => s.addOrUpdateTodoOnLastAssistantMessage,
  );
  const setActivityRunStatus = useChatStore((s) => s.setActivityRunStatus);
  const setWorkflowRunStatus = useChatStore((s) => s.setWorkflowRunStatus);
  const setPlannedWorkflow = useChatStore((s) => s.setPlannedWorkflow);
  const updateAgentStore = useAgentStore((s) => s.updateAgentStore);

  return useCallback(async () => {
    await ensureTourI18nReady();

    const handleNavigate = (path: string) => {
      navigate(path);
    };

    const handleInjectMockData = (
      messages: any[],
      workflow: any,
      options?: { showPlanning?: boolean; thinking?: string | null },
    ) => {
      // Inject mock messages into chat store
      messages.forEach((msg) => {
        if (msg.isStreaming && options?.showPlanning) {
          // Add message first, then mark as streaming + attach todos
          addMessage({
            content: msg.content || "",
            role: msg.role,
          });

          setTimeout(() => {
            updateChatStore((state) => {
              const updatedMessages = [...state.messages];
              for (let i = updatedMessages.length - 1; i >= 0; i--) {
                const m = updatedMessages[i];
                if (
                  m?.role === "assistant" &&
                  !(m as any).isStreaming &&
                  !m.content
                ) {
                  (updatedMessages[i] as any) = {
                    ...m,
                    isStreaming: true,
                    toDos: msg.toDos || [],
                  };
                  break;
                }
              }
              return { messages: updatedMessages };
            });
          }, 50);
        } else if (msg.content) {
          addMessage({
            content: msg.content,
            role: msg.role,
          });
        }
      });

      if (workflow) {
        setPlannedWorkflow(workflow);
        updateChatStore({
          currentSessionId: "tour:build-first-agent",
        });
        updateAgentStore({ activeAgentId: "tour:build-first-agent" });
      }

      if (options?.thinking !== undefined) {
        setCurrentThinking(options.thinking);
      }
    };

    const handleUpdateThinking = (text: string | null) => {
      setCurrentThinking(text);
    };

    const handleAddTodo = (todo: {
      id: string;
      text: string;
      status: "started" | "completed" | "failed";
    }) => {
      addOrUpdateTodoOnLastAssistantMessage(todo.text, todo.status, todo.id);
    };

    const handleCompleteTodo = (id: string) => {
      updateChatStore((state) => {
        const messagesCopy = [...state.messages];
        for (let i = messagesCopy.length - 1; i >= 0; i--) {
          const msg = messagesCopy[i];
          if (msg?.role === "assistant") {
            const toDos = Array.isArray((msg as any).toDos)
              ? [...(msg as any).toDos]
              : [];
            const todoIndex = toDos.findIndex((t: any) => t.id === id);
            if (todoIndex >= 0) {
              toDos[todoIndex] = { ...toDos[todoIndex], status: "completed" };
              (messagesCopy[i] as any) = { ...msg, toDos };
              break;
            }
          }
        }
        return { messages: messagesCopy };
      });
    };

    const handleSetActivityRunStatus = (status: any[]) => {
      setActivityRunStatus(status);
    };

    const handleSetWorkflowRunStatus = (
      status: "running" | "completed" | "failed",
      runId?: string | null,
    ) => {
      setWorkflowRunStatus(status, runId);
    };

    const handleMarkStreamingComplete = () => {
      updateChatStore((state) => {
        const updatedMessages = [...state.messages];
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          const msg = updatedMessages[i];
          if (msg?.role === "assistant" && (msg as any).isStreaming) {
            (updatedMessages[i] as any) = {
              ...msg,
              isStreaming: false,
            };
            break;
          }
        }
        return { messages: updatedMessages };
      });
    };

    const handleInjectWorkflow = (workflow: any, finalMessage: string) => {
      addMessage({
        content: finalMessage,
        role: "assistant",
      });

      setPlannedWorkflow(workflow);
      updateChatStore({
        currentSessionId: "tour:build-first-agent",
      });
      updateAgentStore({ activeAgentId: "tour:build-first-agent" });
    };

    const tourConfig = buildTourConfig(
      handleNavigate,
      handleInjectMockData,
      handleUpdateThinking,
      handleAddTodo,
      handleCompleteTodo,
      handleSetActivityRunStatus,
      handleSetWorkflowRunStatus,
      handleMarkStreamingComplete,
      handleInjectWorkflow,
    );

    await runTour(tourConfig);
  }, [
    navigate,
    addMessage,
    updateChatStore,
    setCurrentThinking,
    addOrUpdateTodoOnLastAssistantMessage,
    setActivityRunStatus,
    setWorkflowRunStatus,
    setPlannedWorkflow,
    updateAgentStore,
  ]);
}
